import {useState, useEffect, useMemo, useCallback, useRef} from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  InputBase,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  EditOutlined as EditIcon,
  VisibilityOutlined as PreviewIcon,
  Send as SendIcon,
  People as PeopleIcon,
  DeleteOutline as DeleteIcon,
  DescriptionOutlined as FormIcon,
} from "@mui/icons-material";
import {useNavigate, useLocation} from "react-router-dom";
import DeleteFormDialog from "../componenets/forms/DeleteFormDialog";
import FormBuilderDialog from "../componenets/forms/FormBuilderDialog";
import FormPreviewDialog from "../componenets/forms/FormPreviewDialog";
import SendFormDialog from "../componenets/forms/SendFormDialog";
import FormRecipientsDialog from "../componenets/forms/FormRecipientsDialog";
import {getCurrentDate, epochSecondsToLocalDate} from "../utils/formUtils";
import {formatDateHebrew, getRecipientStats} from "../utils/formUtils";
import {useMessageContext} from "../contexts/MessageContext";
import {useCompany} from "../contexts/CompanyContext";
import {
  getFormTemplates,
  getFormTemplate,
  createFormTemplate,
  deleteFormTemplate,
  assignTemplateToTrainee,
  getFormsByTemplate,
  getTraineesWithFormStatus,
} from "../services/FormTemplateService";
import {Pagination} from "../componenets/ui";
import {t} from "../componenets/dashboard/coach/coachTokens";

/* ── Constants ───────────────────────────────────────────── */
const PAGE_SIZE = 8;

/* ── Table cell presets (match Clients / Exercises) ──────── */
const td = {padding: "10px 12px", borderBottom: `1px solid ${t.borderLight}`, fontSize: "12.5px", color: t.text, verticalAlign: "middle"};
const th = {padding: "10px 12px", fontSize: "10.5px", fontWeight: 700, color: t.t3, borderBottom: `1px solid ${t.border}`,
            background: t.bg, whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: ".06em"};

/* ═══════════════════════════════════════════════════════════
   Forms page
   ═══════════════════════════════════════════════════════════ */
const FormsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {notificationHandler} = useMessageContext();
  const {company} = useCompany();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);

  // Main state
  const [f, setF] = useState({
    forms: [],
    total: 0,
    keyword: query.get("keyword") || "",
    page: +(query.get("page") || 0),
    loading: true,
  });

  // Dialog states
  const [builderOpen, setBuilderOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [recipientsOpen, setRecipientsOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);

  // Debounced search (client-side)
  const [searchInput, setSearchInput] = useState(f.keyword);
  const debounceRef = useRef(null);
  const handleSearch = useCallback((val) => {
    setSearchInput(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => set({keyword: val}), 300);
  }, []);

  // URL sync
  const sync = (ns) => {
    const p = new URLSearchParams();
    if (ns.keyword) p.set("keyword", ns.keyword);
    if (ns.page > 0) p.set("page", ns.page);
    navigate(p.toString() ? `${location.pathname}?${p}` : location.pathname, {replace: true});
  };

  const set = (patch) => { const ns = {...f, page: 0, ...patch}; setF(ns); sync(ns); };

  // ── Fetch form templates ──────────────────────────────
  const fetchFormTemplates = useCallback(async () => {
    setF(p => ({...p, loading: true}));
    try {
      const companyId = company?.id || null;
      const response = await getFormTemplates(f.page, PAGE_SIZE, companyId);
      const data = response.data;

      const mappedForms = (data.content || []).map(template => {
        let createdAtFormatted = getCurrentDate();
        if (template.createdDate) {
          const date = new Date(template.createdDate * 1000);
          if (!isNaN(date.getTime()) && date.getFullYear() > 1970) {
            // Local day, not toISOString()'s UTC day — a form created at 01:00 Israel
            // (22:00 UTC the day before) otherwise displays as the previous date.
            createdAtFormatted = epochSecondsToLocalDate(template.createdDate);
          }
        }
        const formsCount = template.formsCount || 0;
        return {
          id: template.id,
          title: template.name,
          logoUrl: template.logoUrl,
          description: template.description,
          createdAt: createdAtFormatted,
          companyId: template.companyId,
          questions: [],
          recipients: formsCount > 0 ? Array(formsCount).fill({id: 0}) : [],
        };
      });

      setF(p => ({...p, forms: mappedForms, total: data.totalElements || 0, loading: false}));
    } catch (error) {
      console.error("Error fetching form templates:", error);
      notificationHandler.error("שגיאה בטעינת הטפסים");
      setF(p => ({...p, forms: [], total: 0, loading: false}));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.page, company?.id]);

  useEffect(() => { fetchFormTemplates(); }, [fetchFormTemplates]);

  // Client-side keyword filtering
  const filteredForms = useMemo(() => {
    if (!f.keyword) return f.forms;
    return f.forms.filter(form => form.title.toLowerCase().includes(f.keyword.toLowerCase()));
  }, [f.forms, f.keyword]);

  const pages = Math.ceil(f.total / PAGE_SIZE);

  // ── Question type mapping (server ↔ local) ────────────
  const mapQuestionTypeFromServer = (type) => {
    const typeMap = {
      FORM_TITLE: "form-title",
      SECTION_TITLE: "section-header",
      YES_NO: "yes-no",
      SHORT_TEXT: "short-text",
      LONG_TEXT: "long-text",
      MULTIPLE_CHOICE: "multiple-choice",
      LINK: "link",
      NUMBER: "number",
      DATE: "date",
      FILE_UPLOAD: "file-upload",
      PDF_ATTACHMENT: "pdf-attachment",
      SIGNATURE: "signature",
      CHECKBOX: "consent-checkbox",
      FREE_TEXT: "free-text",
      EMAIL: "email",
      PHONE: "phone",
      RATING: "rating",
    };
    return typeMap[type] || type.toLowerCase().replace(/_/g, "-");
  };

  const mapQuestionFromServer = (serverQuestion) => {
    const localType = mapQuestionTypeFromServer(serverQuestion.questionType);
    const baseQuestion = {id: `q-${serverQuestion.id}`, type: localType, required: serverQuestion.required || false};

    switch (localType) {
      case "form-title":
      case "section-header":
        return {...baseQuestion, title: serverQuestion.title || ""};
      case "free-text":
        return {...baseQuestion, content: serverQuestion.content || serverQuestion.title || ""};
      case "short-text":
      case "long-text":
      case "number":
      case "date":
      case "email":
      case "phone":
        return {...baseQuestion, question: serverQuestion.question || "", placeholder: serverQuestion.placeholder || ""};
      case "yes-no":
        return {
          ...baseQuestion, question: serverQuestion.question || "",
          showDetailsOnYes: serverQuestion.showDetailsOnYes || false,
          detailsPlaceholder: serverQuestion.detailsPlaceholder || "",
          showDetailsOnNo: serverQuestion.showDetailsOnNo || false,
          detailsPlaceholderNo: serverQuestion.detailsPlaceholderNo || "",
        };
      case "multiple-choice":
        return {...baseQuestion, question: serverQuestion.question || "", options: serverQuestion.options || [], allowMultiple: serverQuestion.allowMultiple || false};
      case "link":
        return {...baseQuestion, question: serverQuestion.question || serverQuestion.title || "", linkUrl: serverQuestion.linkUrl || "", linkDescription: serverQuestion.linkDescription || ""};
      case "file-upload":
        return {...baseQuestion, question: serverQuestion.question || "", allowedTypes: serverQuestion.allowedTypes || ["image", "pdf", "document"]};
      case "pdf-attachment":
        return {...baseQuestion, question: serverQuestion.question || serverQuestion.title || "", pdfUrl: serverQuestion.pdfUrl || "", pdfName: serverQuestion.pdfName || ""};
      case "signature":
        return {...baseQuestion, question: serverQuestion.question || ""};
      case "rating":
        return {...baseQuestion, question: serverQuestion.question || "", maxRating: serverQuestion.maxRating || 5};
      case "consent-checkbox":
        return {...baseQuestion, question: serverQuestion.question || "", consentType: serverQuestion.consentType || "general"};
      default:
        return {...baseQuestion, question: serverQuestion.question || ""};
    }
  };

  const mapQuestionTypeToServer = (type) => {
    const typeMap = {
      "form-title": "FORM_TITLE",
      "section-header": "SECTION_TITLE",
      "yes-no": "YES_NO",
      "short-text": "SHORT_TEXT",
      "long-text": "LONG_TEXT",
      "multiple-choice": "MULTIPLE_CHOICE",
      link: "LINK",
      number: "NUMBER",
      date: "DATE",
      "file-upload": "FILE_UPLOAD",
      "pdf-attachment": "PDF_ATTACHMENT",
      signature: "SIGNATURE",
      "consent-checkbox": "CHECKBOX",
      "free-text": "FREE_TEXT",
      email: "EMAIL",
      phone: "PHONE",
      rating: "RATING",
    };
    return typeMap[type] || type.toUpperCase().replace(/-/g, "_");
  };

  const mapQuestionToServer = (question, index) => {
    const questionType = mapQuestionTypeToServer(question.type);
    const baseQuestion = {orderIndex: index, questionType, required: question.required || false};

    switch (question.type) {
      case "form-title":
      case "section-header":
        return {orderIndex: index, questionType, title: question.title || ""};
      case "free-text":
        return {orderIndex: index, questionType, content: question.content || question.title || "טקסט חופשי"};
      case "short-text":
      case "long-text":
      case "number":
      case "date":
        return {...baseQuestion, question: question.question || "", placeholder: question.placeholder || null};
      case "email":
      case "phone":
        return {...baseQuestion, question: question.question || "", placeholder: question.placeholder || null};
      case "yes-no":
        return {
          ...baseQuestion, question: question.question || "",
          showDetailsOnYes: question.showDetailsOnYes || false,
          detailsPlaceholder: question.showDetailsOnYes ? (question.detailsPlaceholder || "פירוט (אופציונלי)") : null,
          showDetailsOnNo: question.showDetailsOnNo || false,
          detailsPlaceholderNo: question.showDetailsOnNo ? (question.detailsPlaceholderNo || "פירוט (אופציונלי)") : null,
        };
      case "multiple-choice":
        return {...baseQuestion, question: question.question || "", options: question.options || [], allowMultiple: question.allowMultiple || false};
      case "link":
        return {...baseQuestion, question: question.question || question.linkDescription || "קישור", linkUrl: question.linkUrl || null, linkDescription: question.linkDescription || null};
      case "file-upload":
        return {...baseQuestion, question: question.question || "", allowedTypes: question.allowedTypes || ["image", "pdf", "document"]};
      case "pdf-attachment":
        return {...baseQuestion, question: question.question || question.pdfName || "קובץ PDF מצורף", pdfUrl: question.pdfUrl || null, pdfName: question.pdfName || null};
      case "signature":
        return {...baseQuestion, question: question.question || ""};
      case "rating":
        return {...baseQuestion, question: question.question || "", maxRating: question.maxRating || 5};
      case "consent-checkbox":
        return {...baseQuestion, question: question.question || ""};
      default:
        return {...baseQuestion, question: question.question || ""};
    }
  };

  const validateQuestions = (questions) => {
    const errors = [];
    questions.forEach((q, index) => {
      const questionNum = index + 1;
      switch (q.type) {
        case "link":
          if (!q.linkUrl || q.linkUrl.trim() === "") errors.push(`שאלה ${questionNum} (קישור): יש להזין כתובת URL`);
          break;
        case "pdf-attachment":
          if (!q.pdfUrl || q.pdfUrl.trim() === "") errors.push(`שאלה ${questionNum} (קובץ PDF): יש להעלות קובץ PDF`);
          break;
        case "multiple-choice":
          if (!q.options || q.options.length < 2) errors.push(`שאלה ${questionNum} (בחירה מרובה): יש להוסיף לפחות 2 אפשרויות`);
          break;
        default:
          break;
      }
    });
    return errors;
  };

  // ── Handlers ──────────────────────────────────────────
  const handleCreateNew = () => { setSelectedForm(null); setBuilderOpen(true); };

  const handleEdit = async (form) => {
    try {
      const response = await getFormTemplate(form.id, form.companyId || company?.id);
      const fullTemplate = response.data;
      const mappedQuestions = (fullTemplate.questions || []).sort((a, b) => a.orderIndex - b.orderIndex).map(mapQuestionFromServer);
      setSelectedForm({
        id: fullTemplate.id, title: fullTemplate.name, logoUrl: fullTemplate.logoUrl, description: fullTemplate.description,
        createdAt: fullTemplate.createdDate ? epochSecondsToLocalDate(fullTemplate.createdDate) : getCurrentDate(),
        lastUpdated: fullTemplate.createdDate ? epochSecondsToLocalDate(fullTemplate.createdDate) : getCurrentDate(),
        companyId: fullTemplate.companyId, questions: mappedQuestions, recipients: [],
      });
      setBuilderOpen(true);
    } catch (error) {
      console.error("Error fetching form template:", error);
      notificationHandler.error("שגיאה בטעינת הטופס");
    }
  };

  const handlePreview = async (form) => {
    try {
      const response = await getFormTemplate(form.id, form.companyId || company?.id);
      const fullTemplate = response.data;
      const mappedQuestions = (fullTemplate.questions || []).sort((a, b) => a.orderIndex - b.orderIndex).map(mapQuestionFromServer);
      setSelectedForm({
        id: fullTemplate.id, title: fullTemplate.name, logoUrl: fullTemplate.logoUrl, description: fullTemplate.description,
        createdAt: fullTemplate.createdDate ? epochSecondsToLocalDate(fullTemplate.createdDate) : getCurrentDate(),
        lastUpdated: fullTemplate.createdDate ? epochSecondsToLocalDate(fullTemplate.createdDate) : getCurrentDate(),
        companyId: fullTemplate.companyId, questions: mappedQuestions, recipients: [],
      });
      setPreviewOpen(true);
    } catch (error) {
      console.error("Error fetching form template for preview:", error);
      notificationHandler.error("שגיאה בטעינת הטופס");
    }
  };

  const handleSend = async (form) => {
    setSelectedForm(form);
    setSendOpen(true);
    setRecipientsLoading(true);
    try {
      const companyId = company?.id || form.companyId;
      const response = await getTraineesWithFormStatus(form.id, companyId);
      const trainees = response.data?.content || [];
      const recipients = trainees.filter(t => t.formStatus !== "NEVER_ASSIGNED").map(t => ({
        id: t.id, name: t.fullName,
        status: t.formStatus === "IN_PROGRESS" ? "PARTIAL_ANSWERED" : t.formStatus,
        assignedDate: t.assignedDate ? t.assignedDate * 1000 : null,
        completedDate: t.completedDate ? t.completedDate * 1000 : null,
      }));
      setSelectedForm({...form, recipients});
    } catch (error) {
      console.error("Error fetching recipients:", error);
    } finally {
      setRecipientsLoading(false);
    }
  };

  const handleViewRecipients = async (form) => {
    try {
      const response = await getFormsByTemplate(form.id, 0, 1000);
      const forms = response.data?.content || [];
      const recipients = forms.map(f => ({
        id: f.traineeId, formId: f.id, name: f.traineeName,
        answered: f.status === "COMPLETED",
        answeredDate: f.completedDate ? f.completedDate * 1000 : null,
      }));
      setSelectedForm({...form, recipients});
      setRecipientsOpen(true);
    } catch (error) {
      console.error("Error fetching recipients:", error);
      notificationHandler.error("שגיאה בטעינת רשימת הנמענים");
    }
  };

  const handleDeleteClick = (form) => { setSelectedForm(form); setDeleteOpen(true); };

  const handleDeleteConfirm = async () => {
    if (selectedForm) {
      try {
        await deleteFormTemplate(selectedForm.id, company?.id);
        notificationHandler.success("הטופס נמחק בהצלחה");
        fetchFormTemplates();
      } catch (error) {
        console.error("Error deleting form template:", error);
        if (error.response?.status === 500 || error.response?.status === 409) {
          notificationHandler.error("לא ניתן למחוק את הטופס כיוון שהוא נשלח למתאמנים. יש למחוק קודם את הטפסים שנשלחו.");
        } else {
          notificationHandler.error("שגיאה במחיקת הטופס");
        }
      }
    }
    setDeleteOpen(false);
    setSelectedForm(null);
  };

  const handleSaveForm = async (formData) => {
    const validationErrors = validateQuestions(formData.questions || []);
    if (validationErrors.length > 0) {
      validationErrors.forEach(error => notificationHandler.error(error));
      return;
    }
    try {
      const serverQuestions = (formData.questions || []).map((q, index) => mapQuestionToServer(q, index));
      const templateData = {name: formData.title, logoUrl: formData.logoUrl || null, description: formData.description || "", questions: serverQuestions};
      await createFormTemplate(templateData, company?.id);
      notificationHandler.success(selectedForm ? "עותק חדש של הטופס נוצר בהצלחה" : "הטופס נוצר בהצלחה");
      fetchFormTemplates();
      setBuilderOpen(false);
      setSelectedForm(null);
    } catch (error) {
      console.error("Error saving form template:", error);
      if (error.response?.status === 400 && !company?.id) {
        notificationHandler.error("יש לבחור חברה לפני שמירת הטופס");
      } else {
        notificationHandler.error("שגיאה בשמירת הטופס");
      }
    }
  };

  const handleSendForm = async (recipients, isMandatory = false, formName = null) => {
    if (selectedForm && recipients.length > 0) {
      try {
        let successCount = 0;
        let alreadyAssignedCount = 0;
        for (const recipient of recipients) {
          try {
            await assignTemplateToTrainee(selectedForm.id, recipient.id, formName, isMandatory);
            successCount++;
          } catch (error) {
            if (error.response?.status === 409) alreadyAssignedCount++;
            else console.error(`Error assigning template to trainee ${recipient.id}:`, error);
          }
        }
        if (successCount > 0) {
          notificationHandler.success(`הטופס נשלח ל-${successCount} מתאמנים`);
          fetchFormTemplates();
        } else if (alreadyAssignedCount > 0) {
          notificationHandler.info("כל המתאמנים שנבחרו כבר קיבלו את הטופס");
        }
      } catch (error) {
        console.error("Error sending form:", error);
        notificationHandler.error("שגיאה בשליחת הטופס");
      }
    }
    setSendOpen(false);
    setSelectedForm(null);
  };

  /* ── Render ──────────────────────────────────────────── */
  return (
      <Box sx={{display: "flex", flexDirection: "column", gap: "18px"}}>

        {/* ─── Title row (on canvas) ──────────────────────── */}
        <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0}}>
          <Box>
            <Typography sx={{fontSize: {xs: "22px", md: "26px"}, fontWeight: 800, color: t.text, lineHeight: 1.2, letterSpacing: "-0.3px"}}>טפסים</Typography>
            <Typography sx={{fontSize: "13px", color: t.t2, mt: 0.25}}>ניהול טפסים</Typography>
          </Box>
          <Box component="button" onClick={handleCreateNew} sx={{
            display: "inline-flex", alignItems: "center", gap: "5px",
            background: t.green, color: "#fff", height: 34, px: "15px", borderRadius: t.rs,
            fontSize: "13px", fontWeight: 600, letterSpacing: "-0.1px",
            border: "none", cursor: "pointer", fontFamily: "inherit", transition: "all .14s",
            "&:hover": {background: t.greenDark, transform: "translateY(-1px)", boxShadow: "0 4px 12px rgba(1,187,167,.35)"},
          }}>
            <AddIcon sx={{fontSize: 16}}/>
            טופס חדש
          </Box>
        </Box>

        {/* ─── Table card ──────────────────────────────────── */}
        <Box sx={{background: t.white, border: `1px solid ${t.border}`, borderRadius: t.r, boxShadow: t.sh, overflow: "hidden", display: "flex", flexDirection: "column"}}>

          {/* Filter bar */}
          <Box sx={{
            display: "flex", alignItems: "center", gap: "7px",
            padding: "10px 14px", borderBottom: `1px solid ${t.border}`,
            background: t.bg, flexWrap: "wrap", flexShrink: 0, direction: "rtl",
          }}>
            <Box sx={{
              display: "inline-flex", alignItems: "center", gap: "4px",
              fontSize: "12px", fontWeight: 700, color: t.greenText,
              background: t.greenLight, border: "1px solid rgba(1,187,167,.2)",
              padding: "4px 12px", borderRadius: "20px", whiteSpace: "nowrap",
            }}>
              <FormIcon sx={{fontSize: 14}}/>
              {f.total} טפסים
            </Box>
            <Box sx={{position: "relative"}}>
              <SearchIcon sx={{position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: t.t3, pointerEvents: "none"}}/>
              <InputBase
                  value={searchInput}
                  onChange={e => handleSearch(e.target.value)}
                  placeholder="חיפוש לפי שם..."
                  sx={{
                    width: 220, padding: "6px 10px 6px 30px",
                    border: `1.5px solid ${t.border}`, borderRadius: t.rs,
                    background: t.white, color: t.text, fontSize: "12px",
                    "& input": {padding: 0},
                    "& input::placeholder": {color: t.t3, opacity: 1},
                    "&:focus-within": {borderColor: t.green, boxShadow: "0 0 0 3px rgba(1,187,167,.08)"},
                  }}
              />
            </Box>
          </Box>

          {/* Table area */}
          {f.loading && !f.forms.length ? (
              <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", minHeight: 540}}>
                <CircularProgress size={32} sx={{color: t.green}}/>
              </Box>
          ) : !filteredForms.length ? (
              <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", minHeight: 240, flexDirection: "column", gap: 1, padding: "48px"}}>
                <FormIcon sx={{fontSize: "48px", color: t.border}}/>
                <Typography sx={{color: t.t3, fontSize: "15px", fontWeight: 500}}>
                  {f.keyword ? "לא נמצאו טפסים" : "אין טפסים עדיין"}
                </Typography>
                <Typography sx={{color: t.t3, fontSize: "13px"}}>
                  {f.keyword ? "נסה לחפש עם מילות מפתח אחרות" : 'לחץ על "טופס חדש" כדי ליצור'}
                </Typography>
              </Box>
          ) : (
              <>
                <TableContainer sx={{overflowY: "auto", maxHeight: 540, direction: "rtl"}}>
                  <Table stickyHeader sx={{tableLayout: "fixed"}}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{...th, textAlign: "right"}}>שם הטופס</TableCell>
                        <TableCell sx={{...th, textAlign: "center", width: "14%", display: {xs: "none", md: "table-cell"}}}>נשלח ל</TableCell>
                        <TableCell sx={{...th, textAlign: "center", width: "14%", display: {xs: "none", md: "table-cell"}}}>תאריך יצירה</TableCell>
                        <TableCell sx={{...th, textAlign: "center", width: 170}}>פעולות</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredForms.map(form => {
                        const recipientStats = getRecipientStats(form.recipients);
                        return (
                            <TableRow key={form.id}
                                      sx={{"&:hover td": {background: t.greenLight}, transition: "background .12s ease"}}>
                              {/* Title */}
                              <TableCell sx={{...td, textAlign: "right"}}>
                                <Typography sx={{fontSize: "12.5px", fontWeight: 600, color: t.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>
                                  {form.title}
                                </Typography>
                              </TableCell>

                              {/* Recipients */}
                              <TableCell sx={{...td, textAlign: "center", display: {xs: "none", md: "table-cell"}}}>
                                {recipientStats.total > 0 ? (
                                    <Box component="button" onClick={() => handleViewRecipients(form)} sx={{
                                      display: "inline-flex", alignItems: "center", gap: "5px",
                                      background: t.bg, border: `1px solid ${t.border}`, borderRadius: "20px",
                                      height: 26, px: "11px", fontSize: 12, fontWeight: 600, color: t.t2,
                                      cursor: "pointer", fontFamily: "inherit", transition: "all .14s",
                                      "&:hover": {background: t.greenLight, borderColor: t.green, color: t.green},
                                    }}>
                                      <PeopleIcon sx={{fontSize: 15}}/>
                                      {recipientStats.total} מתאמנים
                                    </Box>
                                ) : <Typography sx={{fontSize: "12.5px", color: t.t3}}>—</Typography>}
                              </TableCell>

                              {/* Date */}
                              <TableCell sx={{...td, textAlign: "center", display: {xs: "none", md: "table-cell"}}}>
                                <Typography sx={{fontSize: "12.5px", fontWeight: 500, color: t.t2}}>
                                  {formatDateHebrew(form.createdAt)}
                                </Typography>
                              </TableCell>

                              {/* Actions */}
                              <TableCell sx={{...td, textAlign: "center", width: 170}}>
                                <Box sx={{display: "inline-flex", justifyContent: "center", gap: "2px"}}>
                                  <Tooltip title="עריכה">
                                    <IconButton size="small" onClick={() => handleEdit(form)} sx={{color: t.t2, "&:hover": {color: t.green, bgcolor: t.greenLight}}}>
                                      <EditIcon fontSize="small"/>
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="תצוגה מקדימה">
                                    <IconButton size="small" onClick={() => handlePreview(form)} sx={{color: t.t2, "&:hover": {color: t.green, bgcolor: t.greenLight}}}>
                                      <PreviewIcon fontSize="small"/>
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="שליחה למתאמנים">
                                    <IconButton size="small" onClick={() => handleSend(form)} sx={{color: t.t2, "&:hover": {color: t.green, bgcolor: t.greenLight}}}>
                                      <SendIcon fontSize="small"/>
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="מחיקה">
                                    <IconButton size="small" onClick={() => handleDeleteClick(form)} sx={{color: t.red, "&:hover": {bgcolor: t.redLight}}}>
                                      <DeleteIcon fontSize="small"/>
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </TableCell>
                            </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {pages > 1 && (
                    <Pagination currentPage={f.page} totalPages={pages} totalItems={f.total} itemsPerPage={PAGE_SIZE}
                                onPageChange={p => { const ns = {...f, page: p}; setF(ns); sync(ns); }}/>
                )}
              </>
          )}
        </Box>

        {/* ─── Dialogs ─────────────────────────────────── */}
        <FormBuilderDialog open={builderOpen} onClose={() => { setBuilderOpen(false); setSelectedForm(null); }} onSave={handleSaveForm} editingForm={selectedForm}/>
        <FormPreviewDialog open={previewOpen} onClose={() => { setPreviewOpen(false); setSelectedForm(null); }} form={selectedForm}/>
        <SendFormDialog open={sendOpen} onClose={() => { setSendOpen(false); setSelectedForm(null); setRecipientsLoading(false); }} onSend={handleSendForm} form={selectedForm} recipientsLoading={recipientsLoading}/>
        <FormRecipientsDialog open={recipientsOpen} onClose={() => { setRecipientsOpen(false); setSelectedForm(null); }} form={selectedForm}/>
        <DeleteFormDialog open={deleteOpen} onClose={() => { setDeleteOpen(false); setSelectedForm(null); }} onConfirm={handleDeleteConfirm} formTitle={selectedForm?.title}/>
      </Box>
  );
};

export default FormsPage;
